/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesUpdateSubject } from "../funcs/workloadIdentitiesUpdateSubject.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateWorkloadSubjectRequest,
  UpdateWorkloadSubjectSecurity,
} from "../models/operations/updateworkloadsubject.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateWorkloadSubjectMutationVariables = {
  request: UpdateWorkloadSubjectRequest;
  security?: UpdateWorkloadSubjectSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateWorkloadSubjectMutationData = WorkloadIdentityPolicy;

export type UpdateWorkloadSubjectMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateSubject workloadIdentities
 *
 * @remarks
 * Edit an admitted subject's label, tags, or assigned agent. Omitted fields are left unchanged. The subject, match kind, issuer, and tier are fixed at admission. The agent assignment is shared by every admission of the same subject under the same issuer, at either tier, so reassigning it through one admission reassigns it for both. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
 */
export function useUpdateWorkloadSubjectMutation(
  options?: MutationHookOptions<
    UpdateWorkloadSubjectMutationData,
    UpdateWorkloadSubjectMutationError,
    UpdateWorkloadSubjectMutationVariables
  >,
): UseMutationResult<
  UpdateWorkloadSubjectMutationData,
  UpdateWorkloadSubjectMutationError,
  UpdateWorkloadSubjectMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateWorkloadSubjectMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateWorkloadSubject(): MutationKey {
  return ["@gram/client", "workloadIdentities", "updateSubject"];
}

export function buildUpdateWorkloadSubjectMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateWorkloadSubjectMutationVariables,
  ) => Promise<UpdateWorkloadSubjectMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateWorkloadSubject(),
    mutationFn: function updateWorkloadSubjectMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateWorkloadSubjectMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesUpdateSubject(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
